import React, { useEffect, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Loader2, Copy, Check, MapPin, Phone, Globe, Clock, ExternalLink, AlertCircle, Building2, Square, CheckSquare, Lock } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

// Actions Base44 has verified as already live (from the Google search + connected
// integrations). These render as a locked green tick and cannot be unchecked.
const CONFIRMED = {
  'local_seo:citation:google-business-profile': 'Already indexed on Google',
  'local_seo:citation:trustpilot': '4-star Trustpilot profile already live',
  'local_seo:citation:facebook-page': 'Facebook Page already connected',
};

function slug(s) { return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''); }

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Static business facts — kept in sync with LocalBusinessSchema.jsx so the
// NAP shown here matches the LocalBusiness JSON-LD injected on the homepage.
const BUSINESS = {
  name: 'Motion Reality',
  legalName: 'Motion Reality Sim Racing Ltd',
  street: 'Unit 9, Croft Business Park, Carrock Road',
  locality: 'Bromborough',
  region: 'Wirral',
  postcode: 'CH62 3RA',
  country: 'GB',
  website: 'https://motionreality.co.uk',
  email: 'info@motionreality.co.uk',
  latitude: 53.353,
  longitude: -3.001,
  description:
    'Premium motion racing simulator hardware store and cockpit booking platform. Custom sim racing rigs, motion platforms, wheels, pedals and bookable sim racing experiences in Bromborough, near Liverpool.',
  categories: ['Amusement Arcade', 'Video Game Store', 'Hobby Store', 'Sports Equipment Store'],
};

const CITATION_DIRECTORIES = [
  {
    name: 'Google Business Profile',
    url: 'https://www.google.com/business/',
    note: 'The single most important local citation. Claim, verify by postcard, fill every field, add photos, and post weekly updates.',
    primary: true,
  },
  { name: 'Bing Places for Business', url: 'https://www.bingplaces.com/', note: 'Microsoft\'s equivalent — powers Bing & Cortano map results. Import from Google to speed it up.' },
  { name: 'Apple Business Connect', url: 'https://business.apple.com/', note: 'Powers Apple Maps & Siri. Claim and verify your place.' },
  { name: 'Yell.com', url: 'https://www.yell.com/', note: 'Major UK directory. Create a free listing with the exact NAP below.' },
  { name: 'Thomson Local', url: 'https://www.thomsonlocal.com/', note: 'Long-established UK print + web directory.' },
  { name: 'Yelp for Business', url: 'https://business.yelp.co.uk/', note: 'Strong domain authority. Add photos and respond to reviews.' },
  { name: 'Trustpilot', url: 'https://business.trustpilot.com/', note: 'Already used on site — ensure the business profile NAP matches exactly.' },
  { name: 'Hotfrog UK', url: 'https://www.hotfrog.co.uk/', note: 'Free UK business listing.' },
  { name: 'FreeIndex', url: 'https://www.freeindex.co.uk/', note: 'Free UK directory with good local search visibility.' },
  { name: 'Cylex UK', url: 'https://www.cylex-uk.co.uk/', note: 'Free UK business directory.' },
  { name: 'TripAdvisor (Things to Do)', url: 'https://www.tripadvisor.co.uk/BusinessListings', note: 'List as a "Fun & Games" / Things to Do attraction for Bromborough.' },
  { name: 'Facebook Page', url: 'https://www.facebook.com/business/', note: 'Ensure the Page address, phone and hours match the NAP exactly.' },
];

const CLAIM_STEPS = [
  { title: 'Go to Google Business Profile', detail: 'Sign in with a business Google account (not a personal one) at google.com/business and click "Manage now".' },
  { title: 'Enter the exact business name', detail: 'Use "Motion Reality Sim Racing Ltd" — your legal name as Google already shows it. Do not add extra keywords like "Sim Racing Bromborough".' },
  { title: 'Set the category', detail: 'Primary: "Amusement Arcade" or "Video Game Store". Add secondary categories: Hobby Store, Sports Equipment Store.' },
  { title: 'Enter the address', detail: 'Unit 9, Croft Business Park, Bromborough, Wirral. Mark it as a storefront (customers visit).' },
  { title: 'Choose verification', detail: 'Postcard by post is most common (5-14 days). Some locations get instant phone/email verification.' },
  { title: 'Complete every field', detail: 'Hours, phone, website, services, photos (exterior, interior, rigs), and a 750-char description with "sim racing Bromborough" near the start.' },
  { title: 'Add booking link', detail: 'Set the booking action button to point to motionreality.co.uk/bookings.' },
  { title: 'Publish weekly updates & collect reviews', detail: 'Post offers/news each week and ask every customer to leave a GBP review — reviews drive map-pack ranking.' },
];

export default function LocalSeoPanel() {
  const [venue, setVenue] = useState(null);
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState('');
  const [doneMap, setDoneMap] = useState({}); // action_key -> detail
  const [toggling, setToggling] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    let active = true;
    Promise.all([
      base44.entities.VenueSettings.filter({}).then((r) => r?.[0]).catch(() => null),
      base44.entities.CustomerServiceSettings.filter({}).then((r) => r?.[0]).catch(() => null),
      base44.entities.SeoActionLog.list('-created_date', 500).then((logs) => {
        if (!active) return;
        const map = {};
        for (const l of (logs || [])) {
          if (l.action_key?.startsWith('local_seo:')) map[l.action_key] = l.detail || 'Done';
        }
        setDoneMap(map);
      }).catch(() => {}),
    ]).then(([v, s]) => {
      if (!active) return;
      setVenue(v);
      setService(s);
      setLoading(false);
    });
    return () => { active = false; };
  }, []);

  // Toggle a manual action's done state and persist it to SeoActionLog so it
  // survives refreshes and plan regenerations. Confirmed (verified) items are locked.
  async function toggleDone(key, title) {
    if (CONFIRMED[key]) return;
    setToggling(key);
    try {
      const existing = await base44.entities.SeoActionLog.filter({ action_key: key });
      if (existing?.[0]?.id) {
        await base44.entities.SeoActionLog.delete(existing[0].id);
        setDoneMap((prev) => { const n = { ...prev }; delete n[key]; return n; });
      } else {
        const detail = 'Marked done by admin';
        await base44.entities.SeoActionLog.create({ action_key: key, type: 'manual', title, detail });
        setDoneMap((prev) => ({ ...prev, [key]: detail }));
      }
    } catch (e) {
      toast({ title: 'Could not save', description: e?.message || 'Try again', variant: 'destructive' });
    } finally {
      setToggling('');
    }
  }

  function Tick({ k, title, size = 'w-5 h-5' }) {
    const confirmed = !!CONFIRMED[k];
    const done = confirmed || !!doneMap[k];
    const busy = toggling === k;
    if (confirmed) {
      return (
        <span title={`Verified by Base44 — ${CONFIRMED[k]}`} className={`shrink-0 ${size} rounded-full bg-emerald-500/15 border border-emerald-500/50 text-emerald-400 flex items-center justify-center`}>
          <Check className="w-3 h-3" />
        </span>
      );
    }
    return (
      <button
        onClick={() => toggleDone(k, title)}
        disabled={busy}
        title={done ? 'Marked done — click to undo' : 'Mark as done'}
        className={`shrink-0 ${size} rounded border flex items-center justify-center transition-colors disabled:opacity-50 ${
          done ? 'bg-emerald-500/15 border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/25' : 'border-white/20 text-white/30 hover:border-primary/50 hover:text-primary'
        }`}
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : done ? <Check className="w-3 h-3" /> : <Square className="w-3 h-3" />}
      </button>
    );
  }

  const phone = service?.phone_number || '+44 151 528 3001';
  const website = service?.website_url || BUSINESS.website;
  const openTime = venue?.open_time || '09:00';
  const closeTime = venue?.close_time || '20:00';
  const openDays = venue?.open_days && venue.open_days.length ? venue.open_days : [1, 2, 3, 4, 5, 6];

  const napPlain =
`Motion Reality Sim Racing Ltd
Unit 9, Croft Business Park, Carrock Road
Bromborough, Wirral CH62 3RA
United Kingdom
Phone: ${phone}
Email: info@motionreality.co.uk
Website: ${website}`;

  const napHtml = `<div itemscope itemtype="https://schema.org/LocalBusiness">
  <span itemprop="name">Motion Reality Sim Racing Ltd</span>
  <div itemprop="address" itemscope itemtype="https://schema.org/PostalAddress">
    <span itemprop="streetAddress">Unit 9, Croft Business Park, Carrock Road</span>
    <span itemprop="addressLocality">Bromborough</span>
    <span itemprop="addressRegion">Wirral</span>
    <span itemprop="postalCode">CH62 3RA</span>
    <span itemprop="addressCountry">GB</span>
  </div>
  <span itemprop="telephone">${phone}</span>
  <span itemprop="email">info@motionreality.co.uk</span>
  <a itemprop="url" href="${website}">${website}</a>
</div>`;

  function copy(key, text) {
    navigator.clipboard?.writeText(text).then(
      () => {
        setCopied(key);
        setTimeout(() => setCopied(''), 2000);
        toast({ title: 'Copied', description: 'Paste it into the directory listing unchanged.' });
      },
      () => toast({ title: 'Copy failed', variant: 'destructive' }),
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-white/40">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading venue data…
      </div>
    );
  }

  const hoursLabel = openDays.slice().sort((a, b) => a - b).map((d) => `${DAY_NAMES[d].slice(0, 3)} ${openTime}-${closeTime}`).join(' · ');

  return (
    <div className="space-y-6">
      {/* Intro */}
      <div className="border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-start gap-3">
        <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
        <p className="text-emerald-200/80 text-xs leading-relaxed">
          <span className="text-emerald-300 font-bold">Your business already appears on Google.</span>{' '}
          Google indexes "Motion Reality Sim Racing Ltd" with your Trustpilot reviews and Bromborough address — so this is a
          <span className="font-bold"> claim &amp; complete</span> task, not a create-from-scratch one. Claim the profile, fill every field,
          and paste the NAP below <span className="font-bold">identically</span> across all directories so Google trusts the listing.
        </p>
      </div>

      {/* NAP card */}
      <div className="border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="w-4 h-4 text-primary" />
          <h3 className="text-white font-bold text-sm tracking-wide">BUSINESS NAP — COPY EXACTLY</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-1.5 text-sm">
            {[
              { key: 'name', icon: Building2, text: BUSINESS.legalName, sub: null },
              { key: 'street', icon: MapPin, text: BUSINESS.street, sub: null },
              { key: 'city', icon: MapPin, text: `${BUSINESS.locality}, ${BUSINESS.region} ${BUSINESS.postcode}`, sub: null },
              { key: 'country', icon: MapPin, text: 'United Kingdom', sub: null },
              { key: 'phone', icon: Phone, text: phone, href: `tel:${phone.replace(/\s/g, '')}` },
              { key: 'email', icon: Phone, text: BUSINESS.email, href: `mailto:${BUSINESS.email}` },
              { key: 'website', icon: Globe, text: website, href: website, external: true },
              { key: 'hours', icon: Clock, text: hoursLabel, sub: null },
              { key: 'coords', icon: MapPin, text: `${BUSINESS.latitude}, ${BUSINESS.longitude}`, sub: null },
            ].map((r) => {
              const Icon = r.icon;
              const isCopied = copied === r.key;
              return (
                <button
                  key={r.key}
                  onClick={() => copy(r.key, r.text)}
                  title="Click to copy"
                  className="group w-full flex items-start gap-2 text-left rounded transition-colors hover:bg-white/[0.03] px-1 py-0.5"
                >
                  <Icon className="w-4 h-4 text-white/30 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className={`block ${r.key === 'name' ? 'text-white font-bold' : 'text-white/60'}`}>{r.text}</span>
                  </div>
                  {isCopied
                    ? <Check className="w-3.5 h-3.5 text-emerald-400 mt-1 shrink-0" />
                    : <Copy className="w-3.5 h-3.5 text-white/0 group-hover:text-white/40 mt-1 shrink-0 transition-colors" />}
                </button>
              );
            })}
          </div>
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-white/40 text-[10px] font-bold tracking-widest">PLAIN-TEXT NAP</label>
                <button onClick={() => copy('nap', napPlain)} className="text-primary hover:text-primary/80 text-[10px] font-bold tracking-widest flex items-center gap-1">
                  {copied === 'nap' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} COPY
                </button>
              </div>
              <pre className="text-white/70 text-xs whitespace-pre-wrap bg-black/30 border border-white/10 p-3 rounded">{napPlain}</pre>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-white/40 text-[10px] font-bold tracking-widest">SCHEMA MICRODATA (for directories that accept HTML)</label>
                <button onClick={() => copy('html', napHtml)} className="text-primary hover:text-primary/80 text-[10px] font-bold tracking-widest flex items-center gap-1">
                  {copied === 'html' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} COPY
                </button>
              </div>
              <pre className="text-white/70 text-xs whitespace-pre-wrap bg-black/30 border border-white/10 p-3 rounded max-h-32 overflow-auto">{napHtml}</pre>
            </div>
          </div>
        </div>
      </div>

      {/* Claim checklist */}
      <div className="border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="w-4 h-4 text-primary" />
          <h3 className="text-white font-bold text-sm tracking-wide">CLAIM YOUR GOOGLE BUSINESS PROFILE — STEP BY STEP</h3>
          <a href="https://www.google.com/business/" target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-primary text-[10px] font-bold tracking-widest border border-primary/30 bg-primary/5 hover:bg-primary/10 px-3 py-1.5">
            OPEN GBP <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <ol className="space-y-3">
          {CLAIM_STEPS.map((s, i) => {
            const k = `local_seo:claim:${i}`;
            return (
              <li key={i} className="flex gap-3 items-start">
                <span className="shrink-0 w-6 h-6 rounded-full bg-primary/15 border border-primary/40 text-primary text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <Tick k={k} title={`GBP Claim — ${s.title}`} />
                <div className="flex-1">
                  <p className="text-white font-bold text-sm">{s.title}</p>
                  <p className="text-white/50 text-xs mt-0.5 leading-relaxed">{s.detail}</p>
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {/* Citation directories */}
      <div className="border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center gap-2 mb-4">
          <Globe className="w-4 h-4 text-primary" />
          <h3 className="text-white font-bold text-sm tracking-wide">UK LOCAL CITATION DIRECTORIES</h3>
        </div>
        <p className="text-white/50 text-xs mb-4 leading-relaxed">
          Submit the same NAP to as many of these as possible. Consistency across directories is what signals to Google that your business is real and local.
        </p>
        <div className="grid md:grid-cols-2 gap-3">
          {CITATION_DIRECTORIES.map((d) => {
            const k = `local_seo:citation:${slug(d.name)}`;
            const confirmed = !!CONFIRMED[k];
            const done = confirmed || !!doneMap[k];
            return (
              <a key={d.name} href={d.url} target="_blank" rel="noreferrer" className={`group border p-4 transition-colors ${d.primary ? 'border-primary/40 bg-primary/[0.04] hover:bg-primary/10' : done ? 'border-emerald-500/40 bg-emerald-500/[0.04] hover:bg-emerald-500/[0.08]' : 'border-white/10 bg-black/20 hover:border-primary/40 hover:bg-primary/[0.04]'}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className={`font-bold text-sm ${d.primary ? 'text-primary' : 'text-white'}`}>{d.name}{d.primary && <span className="ml-2 text-[9px] tracking-widest bg-primary text-white px-1.5 py-0.5">START HERE</span>}</p>
                  <div className="flex items-center gap-1.5">
                    {confirmed && <span className="text-[8px] tracking-widest text-emerald-400 border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5 flex items-center gap-1"><Lock className="w-2.5 h-2.5" />VERIFIED</span>}
                    <span onClick={(e) => e.preventDefault()} className="cursor-pointer"><Tick k={k} title={`Local citation — ${d.name}`} size="w-5 h-5" /></span>
                    <ExternalLink className="w-3.5 h-3.5 text-white/30 group-hover:text-primary" />
                  </div>
                </div>
                <p className="text-white/50 text-xs leading-relaxed">{d.note}</p>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
}